import { useState } from 'react';
import { useAuthStore } from '@munawb/hooks';
import { useIncidents, useIncidentAction } from '@munawb/hooks';
import { useNotifications, useMarkNotificationsRead } from '@munawb/hooks';
import { supabase } from '@munawb/supabase';
import type { IncidentRow } from '@munawb/types';
import { RISK_LEVEL_COLORS, formatSlaTime, formatDate } from '../lib/utils';

type FilterStatus = 'all' | 'مفتوح' | 'مصعد' | 'موجه' | 'طلب RCA' | 'مغلق';
type ActionType = 'update' | 'close' | 'escalate' | 'direct' | 'request_rca';

const SUPERVISOR_ROLES  = ['Supervisor', 'HeadOfDept'];
const DUTY_MANAGER_PLUS = ['DutyManager', 'QualitySuper', 'Manager', 'Executive', 'Admin'];

export default function SupervisorPage() {
  const { profile } = useAuthStore();
  const [statusFilter, setStatusFilter] = useState<FilterStatus>('all');
  const [selectedIncident, setSelectedIncident] = useState<IncidentRow | null>(null);
  const [actionType, setActionType]             = useState<ActionType>('update');
  const [actionDesc, setActionDesc]             = useState('');
  const [escalateTo, setEscalateTo]             = useState<'medical_director' | 'executive_director'>('medical_director');
  const [actionError, setActionError]           = useState<string | null>(null);

  const isDutyManagerPlus = profile ? DUTY_MANAGER_PLUS.includes(profile.role) : false;
  const isSupervisorOnly  = profile ? SUPERVISOR_ROLES.includes(profile.role)   : false;

  const { data: incidents = [], isLoading } = useIncidents({
    status: statusFilter === 'all' ? undefined : statusFilter,
    dept_id: isDutyManagerPlus ? undefined : profile?.dept_id ?? undefined,
  });

  const { data: notifications = [] } = useNotifications(profile?.id ?? '', null, true);
  const { mutate: markRead }         = useMarkNotificationsRead(profile?.id ?? '');
  const { mutateAsync: doAction, isPending: actionPending } = useIncidentAction();

  const unreadCount = notifications.filter(n => !n.is_read).length;

  const handleAction = async () => {
    if (!selectedIncident) return;
    setActionError(null);

    try {
      await doAction({
        incident_id: selectedIncident.id,
        action_type: actionType,
        description: actionDesc || undefined,
        escalate_to: actionType === 'escalate' && isDutyManagerPlus ? escalateTo : undefined,
      });
      setSelectedIncident(null);
      setActionDesc('');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'حدث خطأ غير متوقع';
      setActionError(msg);
    }
  };

  const handleSignOut = async () => { await supabase.auth.signOut(); };

  const STATUS_FILTERS: { id: FilterStatus; label: string }[] = [
    { id: 'all',       label: 'الكل' },
    { id: 'مفتوح',    label: 'مفتوح' },
    { id: 'مصعد',     label: 'مصعد' },
    { id: 'موجه',      label: 'موجه' },
    { id: 'طلب RCA',  label: 'طلب RCA' },
    { id: 'مغلق',     label: 'مغلق' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 dark:from-slate-950 dark:to-blue-950">

      {/* Header */}
      <header className="glass-panel border-b border-slate-200/80 dark:border-slate-700/50 sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/munawb-logo.png" alt="" className="w-8 h-8 rounded-xl" />
            <div>
              <span className="font-black text-slate-800 dark:text-white text-sm">مُناوِب</span>
              <span className="mr-2 text-xs text-slate-500 dark:text-slate-400">— {profile?.role}</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              className="relative p-2 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              onClick={() => unreadCount > 0 && markRead(notifications.filter(n => !n.is_read).map(n => n.id))}
            >
              <svg className="w-5 h-5 text-slate-600 dark:text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
              {unreadCount > 0 && (
                <span className="absolute top-1 right-1 w-4 h-4 bg-red-500 rounded-full text-white text-[10px] font-black flex items-center justify-center">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>
            <span className="text-xs font-bold text-slate-600 dark:text-slate-400">{profile?.name}</span>
            <button onClick={handleSignOut} className="btn-secondary text-xs px-3 py-1.5">خروج</button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6">

        {/* Status filter bar */}
        <div className="flex gap-2 flex-wrap mb-5">
          {STATUS_FILTERS.map(f => (
            <button
              key={f.id}
              onClick={() => setStatusFilter(f.id)}
              className={`px-4 py-1.5 rounded-xl font-black text-xs transition-all ${
                statusFilter === f.id
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700 hover:bg-slate-50'
              }`}
            >
              {f.label}
              {f.id !== 'all' && incidents.filter(i => i.status === f.id).length > 0 && (
                <span className={`mr-1.5 px-1.5 rounded-full text-[10px] ${
                  statusFilter === f.id ? 'bg-white/20' : 'bg-slate-100 dark:bg-slate-700'
                }`}>
                  {incidents.filter(i => i.status === f.id).length}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Incidents list */}
        {isLoading ? (
          <div className="glass-panel rounded-2xl p-10 text-center">
            <span className="spinner border-blue-200 border-t-blue-600" />
          </div>
        ) : incidents.length === 0 ? (
          <div className="glass-panel rounded-2xl p-10 text-center">
            <p className="text-slate-400 font-bold text-sm">لا توجد أحداث في هذه الفئة</p>
          </div>
        ) : (
          <div className="space-y-3">
            {incidents.map(inc => (
              <IncidentRow
                key={inc.id}
                incident={inc}
                onSelect={() => setSelectedIncident(inc)}
              />
            ))}
          </div>
        )}
      </main>

      {/* Action Modal */}
      {selectedIncident && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="glass-panel w-full max-w-md rounded-2xl p-6 shadow-2xl">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-base font-black text-slate-800 dark:text-white">إجراء على الحدث</h3>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{selectedIncident.event_id}</p>
              </div>
              <button onClick={() => setSelectedIncident(null)} className="p-1.5 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800">
                <svg className="w-5 h-5 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Action Type Selector */}
            <div className="grid grid-cols-2 gap-2 mb-4">
              {([
                { id: 'update',      label: 'تحديث',    roles: [...SUPERVISOR_ROLES, ...DUTY_MANAGER_PLUS] },
                { id: 'close',       label: 'إغلاق',    roles: DUTY_MANAGER_PLUS },
                { id: 'escalate',    label: 'تصعيد',    roles: [...SUPERVISOR_ROLES, ...DUTY_MANAGER_PLUS] },
                { id: 'direct',      label: 'توجيه',    roles: DUTY_MANAGER_PLUS },
                { id: 'request_rca', label: 'طلب RCA',  roles: DUTY_MANAGER_PLUS },
              ] as { id: ActionType; label: string; roles: string[] }[])
                .filter(a => profile && a.roles.includes(profile.role))
                .map(a => (
                  <button
                    key={a.id}
                    onClick={() => setActionType(a.id)}
                    className={`py-2.5 rounded-xl font-black text-xs border transition-all ${
                      actionType === a.id
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 border-slate-200 dark:border-slate-700'
                    }`}
                  >
                    {a.label}
                  </button>
                ))
              }
            </div>

            {/* Escalate-to selector (DutyManager only) */}
            {actionType === 'escalate' && isDutyManagerPlus && (
              <div className="mb-4 space-y-2">
                <p className="text-xs font-black text-slate-600 dark:text-slate-400 mb-2">جهة التصعيد:</p>
                {([
                  { value: 'medical_director',    label: 'المدير الطبي' },
                  { value: 'executive_director',  label: 'المدير التنفيذي' },
                ] as { value: 'medical_director' | 'executive_director'; label: string }[]).map(opt => (
                  <label key={opt.value} className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all ${
                    escalateTo === opt.value
                      ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-300 dark:border-blue-700'
                      : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700'
                  }`}>
                    <input
                      type="radio"
                      name="escalate-to"
                      value={opt.value}
                      checked={escalateTo === opt.value}
                      onChange={() => setEscalateTo(opt.value)}
                      className="w-4 h-4"
                    />
                    <span className="text-sm font-bold text-slate-800 dark:text-white">{opt.label}</span>
                  </label>
                ))}
              </div>
            )}

            {/* Notes */}
            <div className="mb-4">
              <label className="block text-xs font-black text-slate-600 dark:text-slate-400 mb-1.5">
                ملاحظات / سبب الإجراء
              </label>
              <textarea
                rows={3}
                className="input-field resize-none"
                placeholder="اكتب ملاحظاتك هنا..."
                value={actionDesc}
                onChange={e => setActionDesc(e.target.value)}
              />
            </div>

            {actionError && (
              <div className="mb-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-3 text-center">
                <p className="text-red-600 dark:text-red-400 text-xs font-bold">{actionError}</p>
              </div>
            )}

            <div className="flex gap-3">
              <button onClick={() => setSelectedIncident(null)} className="btn-secondary flex-1">
                إلغاء
              </button>
              <button
                onClick={handleAction}
                disabled={actionPending}
                className={`flex-1 py-2.5 rounded-xl font-black text-sm text-white transition-all disabled:opacity-50 ${
                  actionType === 'close'    ? 'bg-slate-600 hover:bg-slate-700' :
                  actionType === 'escalate' ? 'bg-orange-600 hover:bg-orange-700' :
                  actionType === 'direct'   ? 'bg-purple-600 hover:bg-purple-700' :
                  'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                {actionPending ? <span className="spinner border-white/30 border-t-white" /> : 'تنفيذ'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Sub-components ─────────────────────────────────────────────── */

function IncidentRow({ incident, onSelect }: { incident: IncidentRow; onSelect: () => void }) {
  const colors = RISK_LEVEL_COLORS[incident.risk_level] ?? RISK_LEVEL_COLORS['منخفض'];
  const sla    = formatSlaTime(incident.created_at, incident.sla_minutes, incident.status);

  return (
    <button
      onClick={onSelect}
      className="glass-panel rounded-2xl p-4 w-full text-right hover:shadow-md transition-all group"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs font-black text-slate-400 dark:text-slate-500">{incident.event_id}</span>
            {sla?.breached && (
              <span className="text-[10px] font-black bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 px-1.5 py-0.5 rounded-full">
                تجاوز SLA
              </span>
            )}
          </div>
          <p className="text-sm font-black text-slate-800 dark:text-white truncate">{incident.event_type}</p>
          <p className="text-xs text-slate-500 dark:text-slate-400 truncate mt-0.5">{incident.location}</p>
          <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">{formatDate(incident.created_at)}</p>
        </div>
        <div className="flex flex-col items-end gap-2 shrink-0">
          <span className={`text-xs font-black px-2.5 py-1 rounded-full border ${colors.badge}`}>
            {incident.risk_level}
          </span>
          <StatusBadge status={incident.status} />
        </div>
      </div>
    </button>
  );
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    'مفتوح':       'bg-blue-100   dark:bg-blue-900/30   text-blue-700   dark:text-blue-400',
    'مصعد':        'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400',
    'موجه':         'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400',
    'طلب RCA':     'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400',
    'مغلق':        'bg-slate-100  dark:bg-slate-700     text-slate-500  dark:text-slate-400',
    'RCA Completed':'bg-green-100 dark:bg-green-900/30  text-green-700  dark:text-green-400',
  };
  return (
    <span className={`text-[11px] font-black px-2 py-0.5 rounded-full ${map[status] ?? map['مفتوح']}`}>
      {status}
    </span>
  );
}
